import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { useContent } from "@/lib/content";
import { useLang } from "@/lib/lang";
import Reveal from "@/components/places/Reveal";
import InsightAuthor from "@/components/places/InsightAuthor";
import InsightBlocks from "@/components/places/InsightBlocks";
import SEO from "@/components/places/SEO";
import { pickLangObj, TRANSLATABLE_FIELDS } from "@/lib/pickLang";
import { ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InsightDetail() {
  const { slug } = useParams();
  const { insights: INSIGHTS, author_bios = [] } = useContent();
  const { t, lang } = useLang();
  const I = t.insight || {};
  const iCtx = INSIGHTS.find((i) => i.slug === slug);
  const [fetched, setFetched] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const insightRaw = iCtx || fetched;
  const insight = useMemo(
    () => pickLangObj(insightRaw, lang, TRANSLATABLE_FIELDS.insight),
    [insightRaw, lang]
  );
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (iCtx || fetched) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/content/insights/${slug}`);
        if (!cancelled) setFetched(data);
      } catch (e) {
        if (!cancelled && e?.response?.status === 404) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, iCtx, fetched]);

  const safeBody = useMemo(
    () => insight ? DOMPurify.sanitize(insight.body, {
      ALLOWED_TAGS: ["p", "h2", "h3", "blockquote", "ul", "ol", "li", "strong", "em", "a", "br", "img", "figure", "figcaption"],
      ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "loading", "width", "height", "class"],
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|\/api\/media\/|\/)/i,
    }) : "",
    [insight]
  );

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (notFound) return <Navigate to="/insights" replace />;
  if (!insight) {
    return (
      <section className="theme-light min-h-[60vh] pt-40 pb-20 flex items-center justify-center" data-testid="insight-detail-loading">
        <div className="animate-pulse text-sm uppercase tracking-widest text-neutral-500">Carregando insight…</div>
      </section>
    );
  }
  const related = INSIGHTS.filter((i) => i.topic === insight.topic && i.slug !== insight.slug).slice(0, 3);
  // Resolve author avatar from author_bios (same source used in InsightAuthor bio block).
  const authorBio = author_bios.find(
    (a) => a?.name && insight?.author && a.name.toLowerCase().trim() === insight.author.toLowerCase().trim()
  );
  const authorPhoto = authorBio?.photo || "";
  const authorInitials = (insight.author || "").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <>
      <SEO
        title={insight.meta_title || insight.title}
        description={insight.meta_description || insight.excerpt}
        image={insight.og_image || insight.cover}
        type="article"
        focusKeyword={insight.main_keyword || insight.topic}
        keywords={insight.secondary_keywords || []}
        noindex={!!insight.noindex}
        article={{
          published: insight.published_at || `${insight.year}-01-01`,
          modified: insight.updated_at || insight.published_at,
          author: insight.author,
          section: insight.topic,
          tags: [insight.main_keyword, ...(insight.secondary_keywords || [])].filter(Boolean),
        }}
        breadcrumbs={[
          { name: "Home", url: `${origin}/` },
          { name: "Insights", url: `${origin}/insights` },
          { name: insight.title, url: `${origin}/insights/${insight.slug}` },
        ]}
      />
      <div className="fixed top-[64px] left-0 right-0 h-0.5 bg-black/10 z-40"><div className="h-full bg-accent-yellow" style={{ width: `${progress}%` }} data-testid="reading-progress" /></div>

      <section className="theme-light pt-32 pb-8">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-xs text-neutral-500 mb-6"><Link to="/" className="hover:text-black">Home</Link><ChevronRight className="w-3 h-3" /><Link to="/insights" className="hover:text-black">Insights</Link><ChevronRight className="w-3 h-3" /><span className="text-black">{insight.author}</span></div>
          <Reveal>
            <div className="flex flex-wrap items-center gap-2 mb-6"><span className="text-xs px-2 py-1 bg-black text-white">{insight.topic}</span><span className="text-xs text-neutral-500">{insight.readMin} min · {insight.year}</span></div>
            <h1 className="font-display text-4xl md:text-6xl leading-[1] mb-8">{insight.title}</h1>
            <div className="flex items-center gap-3 text-sm text-neutral-600">
              {authorPhoto ? (
                <img src={authorPhoto} alt={insight.author} loading="lazy" data-testid="insight-author-thumb" className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm" />
              ) : (
                <div data-testid="insight-author-thumb-fallback" className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-orange to-accent-yellow text-white flex items-center justify-center text-xs font-display shadow-sm">{authorInitials || "P"}</div>
              )}
              <div><div className="font-semibold text-black">{insight.author}</div><div className="text-xs">{insight.role}</div></div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="theme-light">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal><div className="aspect-[16/9] overflow-hidden mb-10"><img src={insight.cover} alt={insight.title} className="w-full h-full object-cover" /></div></Reveal>
          {/* Blocos posicionados ANTES do texto do post. Inclui blocos "before"
              e blocos "both" (aparecem em cima E embaixo — útil para PDFs). */}
          {(() => {
            const before = (Array.isArray(insight.content_blocks) ? insight.content_blocks : [])
              .filter((b) => b?.position === "before" || b?.position === "both");
            return before.length > 0 ? (
              <Reveal><div className="mb-10" data-testid="insight-blocks-before"><InsightBlocks blocks={before} insightSlug={insight.slug} /></div></Reveal>
            ) : null;
          })()}
          {/* Article body is sanitized via DOMPurify (see safeBody memo above) with a strict tag/attribute allowlist. */}
          <Reveal><article className="article-content" dangerouslySetInnerHTML={{ __html: safeBody }} /></Reveal>
          {/* Blocos posicionados DEPOIS do texto (default legado). Inclui "after",
              "both" e blocos legados sem position definida. */}
          {(() => {
            const after = (Array.isArray(insight.content_blocks) ? insight.content_blocks : [])
              .filter((b) => !b?.position || b.position === "after" || b.position === "both");
            return after.length > 0 ? (
              <Reveal><div className="mt-10" data-testid="insight-blocks-after"><InsightBlocks blocks={after} insightSlug={insight.slug} /></div></Reveal>
            ) : null;
          })()}
          <Reveal><div className="my-12 bg-black text-white p-8" data-testid="key-learnings">
            <div className="text-xs uppercase tracking-widest text-accent-yellow mb-4">{I.keyLearnings || "Principais aprendizados"}</div>
            <ul className="space-y-2">{(insight.keyLearnings || []).map((k, i) => (<li key={`${insight.slug}-kl-${i}`} className="flex gap-3"><span className="text-accent-yellow font-display">{String(i + 1).padStart(2, "0")}</span><span className="text-sm">{k}</span></li>))}</ul>
          </div></Reveal>
          <Reveal><InsightAuthor insight={insight} /></Reveal>
        </div>
      </section>

      {related.length > 0 && (
      <section className="theme-light pt-20 md:pt-24 pb-16 border-t border-black/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal><h3 className="font-display text-3xl mb-8">{I.related || "Artigos relacionados"}.</h3></Reveal>
          <div className="grid md:grid-cols-3 gap-6">{related.map((r) => (
            <Link key={r.slug} to={`/insights/${r.slug}`} className="group block" data-testid={`related-${r.slug}`}>
              <div className="aspect-[4/3] overflow-hidden bg-neutral-200"><img src={r.cover} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition" /></div>
              <div className="pt-3 font-display text-lg">{r.title}</div>
            </Link>
          ))}</div>
        </div>
      </section>
      )}

      <section className="theme-dark py-24 text-center"><div className="max-w-[1440px] mx-auto px-6 md:px-12"><Reveal>
        <div className="font-display text-4xl md:text-6xl mb-8">Vamos conversar?</div>
        <div className="flex flex-wrap justify-center gap-3"><Link to="/contato" className="bg-accent-yellow text-black btn-sharp rounded-full hover:bg-white transition-colors">Fale com a agência</Link><Link to="/cases" className="border border-white/30 btn-sharp rounded-full hover:bg-white hover:text-black transition-colors">Veja cases relacionados</Link></div>
      </Reveal></div></section>
    </>
  );
}
